import Swal, {SweetAlertOptions} from "sweetalert2";
import {Router} from "@angular/router";
import {Injectable} from "@angular/core";
import {formatNumber} from "@angular/common";
import {DateTime} from "luxon";
import {CalculosDescuento, Cheque, ChequeCotizacion} from "../models/cheque.model";
import {Oferta} from "../models/oferta.model";
import {Order} from "../models/order.model";
import {Transaccion} from "../models/transaccion.model";
import {DateUtils} from "./date-utils";
import {VerificationStatus} from "../models/account-status.model";

declare var $: any;

@Injectable({providedIn: 'root'})
export class SwalUtil {

  constructor(private router: Router) {
  }

  public async verificarCuentaWHandler(handler?: (result) => void) {
    Swal.fire({
      title: 'Atención',
      text: 'Debes verificar tu cuenta para continuar',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Verificar',
      cancelButtonText: `Cancelar`,
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#73867b",
      reverseButtons: true
    }).then(handler)
  }

  public async verificarCuenta() {
    Swal.fire({
      title: 'Atención',
      text: 'Debes verificar tu cuenta para continuar',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Verificar',
      cancelButtonText: `Cancelar`,
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#73867b",
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(['/verify-account'])
      }
    })
  }

  public async verificarIdentidad() {
    Swal.fire({
      title: 'Atención',
      text: 'Debes verificar tu identidad para continuar',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Verificar',
      cancelButtonText: `Cancelar`,
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#73867b",
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(['/home'])
      }
    })
  }

  static fireGenericError(title, message) {
    return Swal.fire({
      icon: 'error',
      title: title,
      text: message,
      confirmButtonColor:'#0a6d37',
      confirmButtonText:'Aceptar'
    })
  }
  static fireGenericMessage(title, message) {
    return Swal.fire({
      icon: 'info',
      title: title,
      text: message,
      confirmButtonColor:'#0a6d37',
      confirmButtonText:'Aceptar'
    })
  }
  static fireGenericHtmlMessage(title, message) {
    return Swal.fire({
      icon: 'info',
      title: title,
      html: message,
      confirmButtonColor:'#0a6d37',
      confirmButtonText:'Aceptar'
    })
  }

  /**
   * Muestra un error devuelto por el backend en un formato legible.
   * Soporta: string, { campo: ["msg", ...] }, { message }, arrays de strings.
   */
  static fireServerError(title, error) {
    const mensajes = SwalUtil.parseServerError(error);
    if (mensajes.length <= 1) {
      return SwalUtil.fireGenericMessage(title, mensajes[0] || 'Ocurrió un error. Reintente más tarde.');
    }
    const html = '<ul style="text-align:left; margin:0; padding-left:1.2em;">'
      + mensajes.map(m => `<li>${SwalUtil.escapeHtml(m)}</li>`).join('')
      + '</ul>';
    return SwalUtil.fireGenericHtmlMessage(title, html);
  }

  static parseServerError(error: any): string[] {
    if (error == null) return [];
    if (typeof error === 'string') {
      try {
        const parsed = JSON.parse(error);
        if (parsed && typeof parsed === 'object') return SwalUtil.parseServerError(parsed);
      } catch (e) { /* no es JSON, se usa tal cual */ }
      return [error.trim()].filter(m => !!m);
    }
    if (Array.isArray(error)) {
      return error.reduce((acc, e) => acc.concat(SwalUtil.parseServerError(e)), [] as string[]);
    }
    if (typeof error === 'object') {
      if (typeof error.message === 'string' && Object.keys(error).length <= 3) return [error.message];
      return Object.keys(error).reduce((acc, k) => acc.concat(SwalUtil.parseServerError(error[k])), [] as string[]);
    }
    return [String(error)];
  }

  private static escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  static fireSuccessMessage(title, message) {
    return Swal.fire({
      icon: 'success',
      title: title,
      text: message,
      confirmButtonColor:'#0a6d37',
      confirmButtonText:'Aceptar'
    })
  }

  static fireGenericConfirmation(title: string, text?: string) {

    let options: SweetAlertOptions = {
      title: title,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonColor:'#0a6d37',
      confirmButtonText: 'Confirmar',
      cancelButtonText: `Cancelar`,
      reverseButtons: true
    };
    if(text) {
      options.text=text;
    }
    return Swal.fire(options);

  }

  static getOperacionDescuentoOptions(calculosDescuento: CalculosDescuento, maximoEsperable: number): SweetAlertOptions {
    let html:string = "" +
      "<table class='table'>" +
      "<tr>" +
      "<td>Monto nominal</td>" +
      "<td style='text-align: right;'>$ " + formatNumber(calculosDescuento?.importeNominal, 'es-AR', '1.2-2') + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Máximo Esperable</td>" +
      "<td style='text-align: right;'>$ " + formatNumber(maximoEsperable, 'es-AR', '1.2-2') + "</td>" +
      "</tr>" +
      "</table>";
    let operacionDesceutnoOptions: SweetAlertOptions = {
      title: "Confirmar Operación de Descuento",
      html: html,
      icon: "warning",
      input: 'text',
      inputLabel: 'Monto Esperado',
      inputPlaceholder: 'Ingresa cuánto deseas recibir por el cheque',
      validationMessage: "Debes ingresar un monto válido",
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#0a6d37",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      didOpen(popup: HTMLElement) {
          var container = $(popup);
          container.find('.swal2-input').maskMoney({thousands:'.', decimal:',', allowZero:true, prefix: '$ '});
      },
      preConfirm: (importeEsperadoInput) => {
        if (!importeEsperadoInput) {
          Swal.showValidationMessage(
            'Debes suministrar un monto'
          )
        }
        if (importeEsperadoInput > maximoEsperable || importeEsperadoInput < 0) {
          Swal.showValidationMessage(
            'El monto suministrado es incorrecto'
          )
        }
        let cleanValue = $('.swal2-input').maskMoney('unmasked')[0];
        return cleanValue;
      }
    };
    return operacionDesceutnoOptions;
  }

  static getOperacionDescuentoConfirmOptions(cheque: Cheque, calculosDescuento:CalculosDescuento, importeEsperado:number) {

    let html = "" +
      "<table class='table'>" +
      "<tr><td>Importe</td><td style='text-align: right;'>$ "+formatNumber(calculosDescuento?.importeNominal, "es-AR", '1.2-2') +"</td></tr>" +
      "<tr><td>Vence</td><td style='text-align: right;'>"+DateTime.fromMillis(cheque.dueDate).toFormat('dd/MM/yyyy')+"</td></tr>" +
      "<tr><td>Deseas obtener</td><td style='text-align: right;font-weight: bold'>$ "+formatNumber(importeEsperado, "es-AR", '1.2-2')+"</td></tr>" +
      "</table>";
    let options: SweetAlertOptions = {
      title: 'Estás Publicando el Cheque N°' + cheque.number,
      html: html,
      confirmButtonText: "Confirmar",
      confirmButtonColor: "#0a6d37",
      denyButtonText: 'Cancelar',
      denyButtonColor: "#0a6d37",
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      icon:"warning",
      imageHeight: 10,
      imageWidth: 10,
      reverseButtons: true
    };
    return options;

  }

  static getReferFriendOptions(): SweetAlertOptions {
    let options: SweetAlertOptions = {
      title: 'Referir a un amigo',
      input: 'email',
      inputLabel: 'E-mail',
      inputPlaceholder: 'Ingresa una dirección de email',
      confirmButtonColor: "#0a6d37",
      validationMessage: "Debes ingresar un email válido",
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }
    return options;
  }

  static getAddSaldoOptions(): SweetAlertOptions {

    let options: SweetAlertOptions = {
      title: 'Agregar Saldo',
      html: 'Estamos procesando tus datos. En breve te brindaremos un CVU y un Alias para que puedas transferir dinero e ingresar saldo a la plataforma',
      icon: 'info',
      cancelButtonColor: "#0a6d37",
      cancelButtonText: "Aceptar"
    };
    return options;

  }

  static getRealizarOfertaOptions(cheque: ChequeCotizacion): SweetAlertOptions {
    const now = new Date();

    // 18:00 today
    const limit = new Date();
    limit.setHours(18, 0, 0, 0);

    const diffMs = limit.getTime() - now.getTime();
    const remainingHours = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));

    const options: SweetAlertOptions = {
      title: "Realizar Oferta para cheque N° " + cheque.number,
      icon: "warning",

      html: `
        <div class="swal-form-container">

          <label for="importeOferta" class="swal2-input-label">
            Monto de la Oferta
          </label>
          <input 
            id="importeOferta" 
            class="swal2-input" 
            placeholder="Ingresa cuánto deseas pagar por el cheque"
            style="width: 80%; margin-top: 0.5rem;"
          >

          <label for="horasOferta" class="swal2-input-label">
            Plazo de la oferta (horas)
          </label>
          <input 
            id="horasOferta" 
            type="number" 
            class="swal2-input" 
            min="1" 
            max="${remainingHours}" 
            placeholder="Máximo ${remainingHours} horas"
            style="width: 80%; margin-top: 0.5rem;"
          >

          <label for="disponibilidadDinero" class="swal2-input-label">
            ¿Cuándo estará disponible el dinero?
          </label>
          <select id="disponibilidadDinero" class="swal2-select" style="width: 80%; margin-top: 0.5rem; border-radius: .1875em; border: 1px solid #d9d9d9; height: 2.625em;">
            <option value="" disabled selected>Disponibilidad</option>
            <option value="0">En el día</option>
            <option value="24">24 horas</option>
            <option value="48">48 horas</option>
            <option value="72">72 horas</option>
            <option value="96">96 horas</option>
          </select>

        </div>
      `,

      validationMessage: "Debes ingresar datos válidos",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      focusCancel: true,
      reverseButtons: true,

      customClass: {
        confirmButton: 'swal-confirm-button',
        cancelButton: 'swal-cancel-button'
      },

      didOpen(popup: HTMLElement) {
        const container = $(popup);

        container.find('#importeOferta').maskMoney({
          thousands: '.',
          decimal: ',',
          allowZero: true,
          prefix: '$ '
        });
      },

      preConfirm: () => {
        const importeInput = $('#importeOferta');
        const horasInput = $('#horasOferta');
        const disponibilidadInput = $('#disponibilidadDinero');

        const importeOferta = importeInput.maskMoney('unmasked')[0];
        const horas = Number(horasInput.val());
        const disponibilidadDinero = disponibilidadInput.val();

        const chequeComision = cheque.operacion?.montoOriginal * 1.21 / 100;
        const valorMax = cheque.operacion?.montoOriginal - chequeComision;

        if (!importeOferta || importeOferta <= 0) {
          Swal.showValidationMessage('Debes suministrar un monto válido');
          return false;
        }
        if (importeOferta > valorMax) {
          Swal.showValidationMessage(
            `La oferta no puede superar el valor máximo del cheque ($${valorMax.toFixed(2)})`
          );
          return false;
        }

        if (horas == null || Number.isNaN(horas) || horas < 1) {
          Swal.showValidationMessage('Debes ingresar una cantidad de horas válida');
          return false;
        }

        if (horas > remainingHours) {
          Swal.showValidationMessage(
            `La cantidad de horas no puede superar las ${remainingHours} horas restantes hasta las 18 hs`
          );
          return false;
        }

        if (!disponibilidadDinero) {
          Swal.showValidationMessage(
            'Debes seleccionar cuándo estará disponible el dinero'
          );
          return false;
        }

        return {
          importeOferta,
          horas,
          disponibilidadDinero
        };
      }
    };

    return options;
  }

  // static getRealizarOfertaOptions(cheque: ChequeCotizacion): SweetAlertOptions {
  //   let options: SweetAlertOptions = {
  //     title: "Realizar Oferta para cheque N° " + cheque.number,
  //     icon: "warning",
  //     input: 'text',
  //     inputLabel: 'Monto de la Oferta',
  //     inputPlaceholder: 'Ingresa cuánto deseas pagar por el cheque',
  //     validationMessage: "Debes ingresar un monto válido",
  //     confirmButtonText: "Confirmar",
  //     cancelButtonText: "Cancelar",
  //     showCancelButton: true,
  //     focusCancel: true,
  //     reverseButtons: true,
  //     customClass: {
  //       confirmButton: 'swal-confirm-button',
  //       cancelButton: 'swal-cancel-button'
  //     },
  //     inputAttributes: {
  //       id: 'myInput'
  //     },
  //     didOpen(popup: HTMLElement) {
  //       var container = $(popup);
  //       container.find('.swal2-input').maskMoney({thousands:'.', decimal:',', allowZero:true, prefix: '$ '});
  //     },
  //     preConfirm: (importeEsperadoInput) => {
  //       const tempInput = importeEsperadoInput.replace('$ ', '').replace('.', '').replace(',', '.');
  //       importeEsperadoInput = Number.parseFloat(tempInput);
  //       if (!importeEsperadoInput || importeEsperadoInput < 0) {
  //         Swal.showValidationMessage(
  //           'Debes suministrar un monto válido'
  //         )
  //       }
  //       if (importeEsperadoInput > cheque.operacion.valorVenta) {
  //         Swal.showValidationMessage(
  //           'El monto ingresado no puede superar el valor solicitado'
  //         )
  //       }
  //       let importeEsperadoInputClean = $('.swal2-input').maskMoney('unmasked')[0];
  //       return importeEsperadoInputClean;
  //     }
  //   }
  //   return options;
  // }

  static getVerificarCuentaOptions(): SweetAlertOptions {

    let options: SweetAlertOptions = {
      title: 'Atención',
      text: 'Debes verificar tu cuenta para continuar',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Entendido',
      cancelButtonText: `Cancelar`,
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#73867b",
      reverseButtons: true
    };
    return options;

  }

  static getVerificarIdentidadOptions(): SweetAlertOptions {

    let options: SweetAlertOptions = {
      title: 'Atención',
      text: 'Para realizar esta operación, debes verificar tu identidad',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Entendido',
      cancelButtonText: `Cancelar`,
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "#73867b",
      reverseButtons: true
    };
    return options;

  }

  static getDetalleOfertaOptions(ofertaById: any, oferta: Oferta): SweetAlertOptions {
    let comisiones = ofertaById.comisiones;
    let imupestos = ofertaById.impuestos;
    let aRecibir = ofertaById.totalaRecibir;
    let html = "<table class='table'>" +
      "<tr>" +
      "<td>Importe Nominal</td>" +
      "<td style='text-align: right;'>$ " + formatNumber(ofertaById.oferta.cheque.importe,"es-AR", "1.2-2") + "</td>" +
      "</tr>" +
      "<tr>" +
      "<tr>" +
      "<td>Total a Recibir <i class='material-icons' style='font-size: 16px' ngbPopover='Popover' triggers='click' popoverTitle='Información'>help</i></td>" +
      "<td style='text-align: right;'>$ " + formatNumber(aRecibir,"es-AR", "1.2-2") + "</td>" +
      "</tr>" +
      "</table>"
      // "<ng-template #popContent>" +
      // "    <table>" +
      // "      <tr><td>Importe</td><td align=\"right\">$ {{calculosDescuento.importeNominal | number:'1.2-2'}}</td></tr>" +
      // "      <tr><td>Comisiones</td><td align=\"right\">($ {{(calculosDescuento.flat + calculosDescuento.tna_I)| number:'1.2-2'}})</td></tr>" +
      // "      <tr><td>Bonificación</td><td align=\"right\">$ {{ calculosDescuento.comisiones | number:'1.2-2' }}</td></tr>" +
      // "      <tr><td>Intereses</td><td align=\"right\">($ {{(calculosDescuento.tna_i)| number:'1.2-2' }})</td></tr>" +
      // "      <tr><td>Impuestos</td><td align=\"right\">($ {{(calculosDescuento.ivaSobreTna_i + calculosDescuento.ivaSobreComisiones + calculosDescuento.otrosImpuestos)| number:'1.2-2'}})</td></tr>" +
      // "      <tr><td colspan=\"2\"><hr style=\"margin-top:5px;margin-bottom:5px;\"></td></tr>" +
      // "      <tr><td>Total a recibir</td><td align=\"right\">$ {{(calculosDescuento.capital)| number:'1.2-2'}}</td></tr>" +
      // "    </table>" +
      // "</ng-template>"
    let options: SweetAlertOptions = {
      title: 'Detalle de Oferta',
      html: html,
      showConfirmButton: oferta.status.id == 'PENDING',
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#0a6d37",
      showDenyButton: oferta.status.id != 'ACCEPTED' && oferta.status.id != 'DECLINED',
      denyButtonText: 'Declinar',
      denyButtonColor: "#0a6d37",
      showCancelButton: true,
      cancelButtonText: 'Volver',
      reverseButtons: true
    };
    return options;
  }

  static getDetalleOrdenOptions(orderById: any, order: Order): SweetAlertOptions {
    let comisiones = orderById.comisiones;
    let imupestos = orderById.impuestos;
    let aRecibir = orderById.totalaRecibir;
    let html = "<table class='table'>" +
      "<tr>" +
      "<td>Importe Nominal</td>" +
      "<td style='text-align: right;'>" + orderById.order.cheque.importe + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Importe</td>" +
      "<td style='text-align: right;'>" + order.amount + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Comisiones</td>" +
      "<td style='text-align: right;color:red'>(" + comisiones + ")</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Impuestos</td>" +
      "<td style='text-align: right;color:red''>(" + imupestos + ")</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Total a Recibir</td>" +
      "<td style='text-align: right;'>" + aRecibir + "</td>" +
      "</tr>" +
      "</table>"
    let options: SweetAlertOptions = {
      title: 'Detalle de Order',
      html: html,
      showConfirmButton: order.status.id == 'PENDING',
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#0a6d37",
      showDenyButton: order.status.id != 'ACCEPTED' && order.status.id != 'DECLINED',
      denyButtonText: 'Declinar',
      denyButtonColor: "#0a6d37",
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }
    return options;
  }

  static getDetalleMovimientoOptions(transaccionById: any, transaccion: Transaccion): SweetAlertOptions {
    let comisiones = transaccionById.comisiones;
    let imupestos = transaccionById.impuestos;
    let aRecibir = transaccionById.totalaRecibir;

    let html = "<table class='table'>" +
      "<tr>" +
      "<td>Importe Nominal</td>" +
      "<td style='text-align: right;'>" + formatNumber(transaccionById.transaccion.cheque.importe,"es-AR", "1.2-2") + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Importe Transacciondo</td>" +
      "<td style='text-align: right;'>" + transaccion + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Comisiones</td>" +
      "<td style='text-align: right;color:red'>(" + comisiones + ")</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Impuestos</td>" +
      "<td style='text-align: right;color:red''>(" + imupestos + ")</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Total a Recibir</td>" +
      "<td style='text-align: right;'>" + aRecibir + "</td>" +
      "</tr>" +
      "</table>"
    let options: SweetAlertOptions = {
      title: 'Detalle de Transaccion',
      html: html,
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#0a6d37",
      denyButtonText: 'Declinar',
      denyButtonColor: "#0a6d37",
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: 'Cancelar',
    };
    return options;
  }

  static getConfirmarDescuentoOptions(chequeFormValue: any, calculosDescuento:CalculosDescuento, bancoDescription:string, tipoChequeDescripcion:string) {

    let html = "" +
      "<table class='table'>" +
      "<tr><td>Tipo</td><td style='text-align: right;'>"+tipoChequeDescripcion+"</td></tr>" +
      "<tr><td>Banco</td><td style='text-align: right;'>"+bancoDescription+"</td></tr>" +
      "<tr><td>Nro. Cheque</td><td style='text-align: right;'>"+chequeFormValue.numero +"</td></tr>" +
      "<tr><td>Importe</td><td style='text-align: right;'>$ "+formatNumber(calculosDescuento?.importeNominal, "es-AR", '1.2-2') +"</td></tr>" +
      "<tr><td>Fecha Emision</td><td style='text-align: right;'>"+chequeFormValue.date+"</td></tr>" +
      "<tr><td>Fecha de Pago</td><td style='text-align: right;'>"+chequeFormValue.dueDate+"</td></tr>" +
      "<tr><td>Razón Social</td><td style='text-align: right;'>"+chequeFormValue.librador+"</td></tr>" +
      "<tr><td>CUIT</td><td style='text-align: right;'>"+chequeFormValue.libradorIdNumber+"</td></tr>" +
      "<tr><td>Recomendado</td><td style='text-align: right;'>$ "+formatNumber(calculosDescuento.capital, "es-AR", '1.2-2')+"</td></tr>" +
      "<tr><td>Esperado</td><td style='text-align: right;'>$ "+formatNumber(chequeFormValue.importeEsperado, "es-AR", '1.2-2')+"</td></tr>" +
      "</table>";
    let options: SweetAlertOptions = {
      title: 'Confirmar Operación de Descuento',
      html: html,
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#0a6d37",
      denyButtonText: 'Declinar',
      denyButtonColor: "#0a6d37",
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: 'Cancelar',
      icon:"warning",
      imageHeight: 10,
      imageWidth: 10,
    };
    return options;

  }

  static getCambiarMontoEsperadoOptions(cheque: Cheque, calculosDescuento:CalculosDescuento): SweetAlertOptions {
    let html = "<table class='table'>" +
      "<tr>" +
      "<td>Monto Nominal</td>" +
      "<td style='text-align: right;'>" + formatNumber(cheque.importe,"es-AR", "1.2-2") + "</td>" +
      "</tr>" +
      "<tr>" +
      "<td>Monto Recomendado</td>" +
      "<td style='text-align: right;'>" + formatNumber(calculosDescuento?.capital,"es-AR", "1.2-2") + "</td>" +
      "</tr>" +
      "</table>"

    let options: SweetAlertOptions = {
      title: "Cambiar monto esperado para cheque N° " + cheque.number,
      icon: "info",
      html:html,
      input: 'text',
      inputLabel: 'Nuevo monto esperado',
      inputPlaceholder: 'Ingresa cuánto deseas obtener por el cheque',
      validationMessage: "Debes ingresar un monto válido",
      confirmButtonColor: "#0a6d37",
      cancelButtonColor: "rgba(95,121,107,0.64)",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      focusCancel: true,
      reverseButtons: true,
      inputAttributes: {
        id: 'myInput'
      },
      didOpen(popup: HTMLElement) {
        var container = $(popup);
        container.find('.swal2-input').maskMoney({thousands:'.', decimal:',', allowZero:true, prefix: '$ '});
      }
    }
    return options;

  }

  static getVerificationStatusOptions (verificationStatus: VerificationStatus, activeValidation): SweetAlertOptions {
    if (activeValidation.kyc === true &&
        (verificationStatus.documentos === 'PENDING' || verificationStatus.documentos === 'DECLINED') &&
        activeValidation.documentacion === false) {
      return {
        title: 'Validación en Proceso',
        html: '<p>Una vez validada tu Identidad, vas a poder seguir validando tus Datos Jurídicos.</p>',
        showConfirmButton: true,
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#0a6d37',
        showCancelButton: false,
        icon: 'warning',
        imageHeight: 10,
        imageWidth: 10,
        reverseButtons: true
      };
    }
    let html = "";
    if( verificationStatus.kyc === 'PENDING' || verificationStatus.kyc === 'DECLINED' || verificationStatus.documentos === 'PENDING' || verificationStatus.documentos === 'DECLINED' ||
    verificationStatus.informacionAdicional === 'PENDING' || verificationStatus.informacionAdicional === 'DECLINED') {
       html = "<p>Para poder comenzar a operar aun necesitamos que nos presente los siguientes datos:</p>"
    }
    if ( (verificationStatus.kyc === 'PENDING' || verificationStatus.kyc === 'DECLINED') && activeValidation.kyc === false){
      html += '<p>Verificar Identidad</p>'
    }
    if ( (verificationStatus.documentos === 'PENDING' || verificationStatus.documentos === 'DECLINED') && activeValidation.documentacion === false){
      html += '<p>Datos Jurídicos</p>'
    }
    if ( (verificationStatus.informacionAdicional === 'PENDING' || verificationStatus.informacionAdicional  === 'DECLINED') && activeValidation.adicional === false){
      html += '<p>Informacion Adicional</p>'
    }
    if (html.length > 0) {
      let options: SweetAlertOptions = {
        title: 'Continuar',
        html: html,
        confirmButtonText: "Continuar Validación",
        confirmButtonColor: "#0a6d37",
        denyButtonText: '',
        denyButtonColor: "#0a6d37",
        showCancelButton: true,
        cancelButtonText: 'Ahora No',
        icon:"warning",
        imageHeight: 10,
        imageWidth: 10,
        reverseButtons: true
    };
      return options;
    } else {
      html = "<p>Su información esta en proceso de Validación. Este proceso puede tardar unos minutos.</p>"
      let options: SweetAlertOptions = {
        title: 'Validación en Proceso ',
        html: html,
        showConfirmButton: true,
        confirmButtonText: 'Aceptar',
        confirmButtonColor: "#0a6d37",
        showCancelButton: false,
        icon:"warning",
        imageHeight: 10,
        imageWidth: 10,
        reverseButtons: true
      };
      return options;
    }
  }

  static getVerificationEmailValidation(){}

  public async getOfertaAlert(horasVto: number ,confirmHandler: () => void): Promise<void> {
    Swal.fire({
      title: 'Atención',
      text: `Al realizar esta oferta, estás comprometiéndote a la compra del cheque en caso de que sea aceptada por el vendedor. Tené en cuenta que tu oferta será válida por un plazo de ${horasVto} horas.`,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#0a6d37',
      denyButtonText: 'Volver',
      denyButtonColor: '#0a6d37',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        confirmHandler();
      }
    });
  }
}

